import { previousDay, streakOn } from "./scoring.js";
import type { DayKey } from "./types.js";

/* ============================================================
   Vale-falta

   O campo existia desde o começo e nunca foi gasto. A ficha nascia com
   dois, o perfil escrevia "🃏 Vale-faltas restantes: 2", o calendário
   desenhava o coringa e o motor de pontos tratava dia de vale-falta como
   dia cumprido — e não havia, em lugar nenhum do app nem do servidor,
   nada que gastasse um. Todo mundo via a promessa e ninguém podia usar.

   Pior: `jokerDays` ficou fora de `scoreFields()` na regra do Firestore
   até agora, e `rescoreMember` lê o campo DA FICHA para decidir a
   sequência. Quem escrevesse o mês inteiro ali ligava o multiplicador de
   1,5× sem postar prato nenhum, com o servidor carimbando. A regra
   fechou; este módulo é o que decide quando o gasto é legítimo.

   Ele mora no core pelo mesmo motivo que a pontuação: a tela precisa
   dizer "dá pra salvar essa sequência" ANTES de pedir, e a função
   precisa recusar sem confiar no cliente. Duas cópias da regra
   divergiriam, e a pessoa veria um botão que o servidor recusa.
   ============================================================ */

/**
 * Com quantos vale-faltas a ficha nasce.
 *
 * Mora aqui porque três lugares precisam do mesmo número: o esquema que
 * cria a ficha, a função que gasta (para ficha antiga sem o campo) e a
 * regra do Firestore, que não consegue importar nada e por isso repete o
 * literal com um comentário apontando para cá. Dois deles divergindo é
 * um saldo que a tela mostra e o servidor não honra.
 */
export const VALE_FALTAS_INICIAIS = 2;

/** Por que não dá. `null` é "dá". */
export type RecusaDoValeFalta =
  | "sem-saldo"
  | "dia-no-futuro"
  | "fora-do-desafio"
  | "desafio-acabou"
  | "ja-postou"
  | "ja-usado";

export const MOTIVO_DA_RECUSA: Record<RecusaDoValeFalta, string> = {
  "sem-saldo": "Você já usou os seus vale-faltas neste desafio.",
  "dia-no-futuro": "Só dá pra cobrir um dia que já passou.",
  "fora-do-desafio": "Esse dia está fora do desafio.",
  "desafio-acabou": "O desafio já terminou.",
  "ja-postou": "Você postou nesse dia — não há falta pra cobrir.",
  "ja-usado": "Esse dia já está coberto por um vale-falta.",
};

export interface PedidoDeValeFalta {
  /** O dia que se quer cobrir. */
  dia: DayKey;
  hoje: DayKey;
  startDay: DayKey;
  /** Vazio = desafio sem data de fim. */
  endDay: DayKey | "";
  /** Dias em que a pessoa postou. */
  days: readonly DayKey[];
  jokerDays: readonly DayKey[];
  jokers: number;
}

/**
 * Pode gastar um vale-falta neste dia?
 *
 * A ordem das recusas é a ordem em que elas interessam a quem lê: saldo
 * primeiro, porque é a única que não muda escolhendo outro dia.
 *
 * HOJE NÃO CONTA como falta, e isso não é detalhe: o dia não acabou, a
 * pessoa ainda pode postar, e gastar um coringa em algo que ela ia fazer
 * de graça é queimar o recurso que existe justamente para o dia perdido.
 * É a mesma conta que a tela já faz na sequência ("ainda não postou hoje
 * não zera a sequência").
 */
export function conferirValeFalta(p: PedidoDeValeFalta): RecusaDoValeFalta | null {
  if (!(Number(p.jokers) > 0)) return "sem-saldo";
  if (!p.dia || !p.hoje) return "dia-no-futuro";
  if (p.dia >= p.hoje) return "dia-no-futuro";
  if (p.startDay && p.dia < p.startDay) return "fora-do-desafio";
  if (p.endDay && p.dia > p.endDay) return "fora-do-desafio";
  /* Desafio terminado não se conserta. O placar dele já foi lido, contado
     e comentado pelo grupo; mexer nele semanas depois é mudar um
     resultado que todo mundo já considera fechado. */
  if (p.endDay && p.endDay < p.hoje) return "desafio-acabou";
  if (p.days.includes(p.dia)) return "ja-postou";
  if (p.jokerDays.includes(p.dia)) return "ja-usado";
  return null;
}

/**
 * O que se salva cobrindo ESTE dia: o tamanho da sequência que volta a
 * existir.
 *
 * É o número que faz a pessoa tocar no botão. Sem ele o convite seria
 * "usar um vale-falta?", que não diz o que se ganha nem o que se perde —
 * e vale-falta é um recurso escasso, então quem não sabe o que ganha
 * guarda para sempre.
 *
 * A conta é a mesma do motor: emenda o dia coberto ao que já existe e
 * conta para trás a partir do dia mais recente cumprido.
 */
export function sequenciaSalva(p: PedidoDeValeFalta): number {
  const cumpridos = new Set<DayKey>([...p.days, ...p.jokerDays, p.dia]);
  /* Conta a partir de HOJE se já postou hoje, senão do próprio dia
     coberto — que é o caso comum (o convite aparece justamente porque a
     pessoa perdeu ontem). */
  const de = cumpridos.has(p.hoje) ? p.hoje : p.dia;
  return streakOn(cumpridos, de);
}

/**
 * O dia que o app deve oferecer: ONTEM, e só ele.
 *
 * O servidor aceita qualquer dia passado do desafio — o limite de
 * verdade são os dois coringas. Mas a TELA oferece um só, porque um
 * convite que pergunta "qual dia?" transforma uma decisão de um toque
 * numa tela de calendário, e porque a falta que dói é sempre a mais
 * recente. Quem quiser cobrir um dia mais antigo faz pelo calendário do
 * perfil, onde escolher dia é o gesto natural.
 */
export const diaParaCobrir = (hoje: DayKey): DayKey => previousDay(hoje);

/* ------------------------------------------------------------
   Trocar pontos por vale-falta

   O ponto acumulado não servia para nada além de ordenar o placar. Esta
   é a primeira coisa que ele compra — e compra a peça que protege a
   sequência, que é o que faz a pessoa voltar amanhã.

   Os limites são o que impede a troca de virar a estratégia do jogo:
   uma por semana (a temporada fecha toda segunda, e trocar não mexe no
   placar da semana — sai do total, não do dia) e um teto de saldo, para
   ninguém estocar uma temporada inteira de faltas.
   ------------------------------------------------------------ */

/** Quanto custa um vale-falta em pontos. */
export const PRECO_DO_VALE_FALTA = 20;
/** Acima disso não dá pra comprar: é para salvar uma falta, não estocar. */
export const TETO_DE_VALE_FALTAS = 3;

export type RecusaDaTroca = "poucos-pontos" | "ja-trocou" | "saldo-cheio" | "desafio-acabou";

export const MOTIVO_DA_TROCA: Record<RecusaDaTroca, string> = {
  "poucos-pontos": `Um vale-falta custa ${PRECO_DO_VALE_FALTA} pontos.`,
  "ja-trocou": "Você já trocou pontos esta semana. Dá de novo na segunda.",
  "saldo-cheio": `Você já tem ${TETO_DE_VALE_FALTAS} vale-faltas guardados.`,
  "desafio-acabou": "O desafio já terminou.",
};

export interface PedidoDeTroca {
  /** O total que a ficha mostra (já com as trocas descontadas). */
  totalPoints: number;
  jokers: number;
  /** Segunda-feira da semana → pontos trocados. */
  trocas: Readonly<Record<string, number>>;
  /** A segunda-feira da semana de hoje. */
  semana: DayKey;
  hoje: DayKey;
  endDay: DayKey | "";
}

export function conferirTroca(p: PedidoDeTroca): RecusaDaTroca | null {
  if (p.endDay && p.endDay < p.hoje) return "desafio-acabou";
  if (Number(p.jokers) >= TETO_DE_VALE_FALTAS) return "saldo-cheio";
  if ((p.trocas[p.semana] ?? 0) > 0) return "ja-trocou";
  if (!(Number(p.totalPoints) >= PRECO_DO_VALE_FALTA)) return "poucos-pontos";
  return null;
}
